import React from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { btnPrimaryClass, btnSecondaryClass } from "../dashboardStyles";

/**
 * Wizard Completion Page
 * Shows success message after completing organization setup. A page in the
 * dashboard's content column, like the wizard steps before it, rather than a
 * dialog over them — and edge to edge on a phone, the same as they are.
 */
const WizardComplete = ({
  bookingUrl = "yourcompany.appointed.com",
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleFinish = () => {
    if (onClose) {
      onClose();
    }
    navigate("/dashboard/organization");
  };

  const handleViewSite = () => {
    window.open(bookingUrl, "_blank");
  };

  return (
    <div className="-mx-4 flex justify-center sm:mx-0 sm:py-6">
      <div className="flex w-full max-w-lg flex-col items-center border-y border-ink-200 bg-white px-5 py-10 text-center sm:rounded-card sm:border sm:px-8 sm:shadow-card">
        <div className="flex size-20 shrink-0 items-center justify-center rounded-full bg-brand-50">
          <i className="bi bi-check-circle-fill text-5xl text-brand-600" />
        </div>

        <h1 className="mt-5 mb-0 text-xl font-bold text-ink-900 sm:text-2xl">
          {t("Dashboard.WizardComplete.title")}
        </h1>

        <p className="mt-2 text-fluid-sm text-ink-500">
          {t("Dashboard.WizardComplete.description")}
        </p>

        <button
          type="button"
          onClick={handleViewSite}
          className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-brand-600 bg-brand-50 px-4 py-3 transition hover:bg-brand-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
          title={t("Dashboard.WizardComplete.openBookingPage")}
        >
          <i className="bi bi-link-45deg shrink-0 text-lg text-brand-600" />
          <span className="truncate text-fluid-sm font-semibold text-brand-800">
            {bookingUrl}
          </span>
        </button>

        <button
          className={`${btnSecondaryClass} mt-3`}
          onClick={handleViewSite}
        >
          <i className="bi bi-eye" /> {t("Dashboard.WizardComplete.viewSite")}
        </button>

        <p className="mt-6 text-fluid-sm text-ink-500">
          {t("Dashboard.WizardComplete.continueDescription")}
        </p>

        {/* Decorative placeholder of the dashboard the user lands on next. */}
        <div
          aria-hidden="true"
          className="mt-4 w-full overflow-hidden rounded-card border border-ink-200"
        >
          <div className="flex items-center border-b border-ink-200 bg-ink-50 px-4 py-3">
            <i className="bi bi-person-circle text-2xl text-ink-300" />
          </div>
          <div className="flex flex-col gap-2.5 p-4">
            <div className="h-2.5 w-4/5 rounded-full bg-ink-100" />
            <div className="h-2.5 w-3/5 rounded-full bg-ink-100" />
            <div className="grid grid-cols-3 gap-2">
              <div className="h-12 rounded-lg bg-ink-100" />
              <div className="h-12 rounded-lg bg-ink-100" />
              <div className="h-12 rounded-lg bg-ink-100" />
            </div>
            <div className="h-2.5 w-2/5 rounded-full bg-ink-100" />
          </div>
        </div>

        <button className={`${btnPrimaryClass} mt-6 w-full`} onClick={handleFinish}>
          {t("Dashboard.WizardComplete.finish")}
        </button>
      </div>
    </div>
  );
};

WizardComplete.propTypes = {
  bookingUrl: PropTypes.string,
  onClose: PropTypes.func,
};

export default WizardComplete;
